"use client";
import { useEffect, useSyncExternalStore } from "react";

const listeners = new Set<() => void>();
const versions = new Map<string, number>();
let resumed = 0;
let channel: BroadcastChannel | undefined;
const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
function notify(eventId?: string) {
  if (eventId) versions.set(eventId, (versions.get(eventId) ?? 0) + 1);
  else resumed++;
  listeners.forEach((listener) => listener());
}
export function invalidateEvent(eventId: string) {
  if (!UUID.test(eventId)) return;
  notify(eventId);
  channel?.postMessage({ eventId }); // Navigation/refresh hint only; never authority or payload.
}
export function useEventInvalidation(eventId?: string) {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    () => `${resumed}:${eventId ? (versions.get(eventId) ?? 0) : 0}`,
    () => "0:0",
  );
}
export function useEventInvalidationBridge() {
  useEffect(() => {
    const connection =
      typeof BroadcastChannel === "undefined"
        ? undefined
        : new BroadcastChannel("wio-veyra.events.v1");
    channel = connection;
    if (connection)
      connection.onmessage = (event) => {
        const id: unknown = event.data?.eventId;
        if (typeof id === "string" && UUID.test(id)) notify(id);
      };
    let last = 0;
    const resume = () => {
      if (document.visibilityState !== "visible" || Date.now() - last < 500)
        return;
      last = Date.now();
      notify();
    };
    window.addEventListener("focus", resume);
    document.addEventListener("visibilitychange", resume);
    return () => {
      window.removeEventListener("focus", resume);
      document.removeEventListener("visibilitychange", resume);
      connection?.close();
      if (channel === connection) channel = undefined;
      versions.clear();
    };
  }, []);
}
